import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@starling/core/api/errors';
import { FormError } from '@/components/form/primitives';
import { useGoogleLoginMutation } from './hooks';
import styles from './GoogleSignInButton.module.css';

// «Войти через Google» (GIS id_token) — блок под формой /login. Логика — как
// в Light (frontend/src/auth/GoogleSignInButton.tsx), визуал — примитивы
// Vision (без AntD):
//   * `VITE_GOOGLE_CLIENT_ID` не задан → компонент не рендерит НИЧЕГО
//     (симметрично бэку, где без GOOGLE_ENABLED эндпоинт отвечает 404);
//   * GIS-скрипт грузится ЛЕНИВО — только здесь и только при заданном
//     client_id (динамический <script>, НЕ глобально в index.html);
//   * One Tap / auto prompt НЕ используем — только явная кнопка;
//   * успех обрабатывает мутация (setTokens) — редирект делает эффект
//     LoginPage на isAuthenticated; ContextRequired гасится внутри хука
//     (авто-select-context, решение V1.3-b);
//   * ошибки — инлайн (в Vision нет тостера).

const GSI_SRC = 'https://accounts.google.com/gsi/client';

export function GoogleSignInButton(): JSX.Element | null {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
  const { t, i18n } = useTranslation();
  const mutation = useGoogleLoginMutation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // GIS-callback живёт вне React-жизненного цикла (регистрируется один раз
  // в initialize) → зовём актуальный обработчик через ref, чтобы не поймать
  // устаревшие t/mutation из замыкания первого рендера.
  const onCredential = (credential: string): void => {
    setError(null);
    mutation.mutate(
      { id_token: credential },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 401) {
            setError(t('auth.errors.googleFailed'));
          } else {
            setError(t('auth.errors.generic'));
          }
        },
      },
    );
  };
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;

    const init = (): void => {
      const gsi = window.google?.accounts.id;
      const container = containerRef.current;
      if (cancelled || !gsi || !container) return;
      gsi.initialize({
        client_id: clientId,
        callback: (resp) => onCredentialRef.current(resp.credential),
      });
      // При смене локали эффект перезапускается — чистим контейнер, чтобы
      // renderButton не оставил рядом старую кнопку.
      container.replaceChildren();
      gsi.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        // NB (приёмка V1, проверено headless-прогоном): современный GIS
        // ИГНОРИРУЕТ locale и рендерит кнопку по Accept-Language браузера
        // (lt-браузер → литовская кнопка при locale:'ru'). Параметр оставлен
        // как заявление намерения + гигиена значений (только ru/kk).
        locale: (i18n.resolvedLanguage ?? i18n.language) === 'kk' ? 'kk' : 'ru',
      });
    };

    // Скрипт уже загружен (повторный визит на /login) — инициализируемся сразу.
    if (window.google?.accounts.id) {
      init();
      return () => {
        cancelled = true;
      };
    }

    // Ленивая загрузка GIS. Защита от двойной вставки (StrictMode/ремаунт):
    // ищем существующий <script> по src, вешаем свой onload-листенер на него.
    let script = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', init);
    return () => {
      cancelled = true;
      script.removeEventListener('load', init);
      // Сам <script> не удаляем: GIS уже мог загрузиться, повторная вставка
      // при следующем визите бессмысленна — переиспользуем.
    };
  }, [clientId, i18n.language]);

  if (!clientId) return null;

  return (
    <>
      <div className={styles.divider}>{t('auth.or')}</div>
      <div ref={containerRef} data-testid="google-signin" className={styles.container} />
      {error && (
        <div className={styles.error}>
          <FormError>{error}</FormError>
        </div>
      )}
    </>
  );
}
